import type { Lot, TaxStatusEnum } from 'venn-api';
import type { SearchMenuItem } from '../../../search-menu';

export type HoldingPeriodInput =
  | { type: 'FromDate'; datePurchased: number | undefined }
  | { type: 'LongTerm' }
  | { type: 'ShortTerm' };

export type LotInput = {
  id: string;
  holdingPeriod: HoldingPeriodInput;
  taxStatus: TaxStatusEnum;
  marketValue: number;
  costBasis: number;
  quantity: number;
  isNew: boolean;
};

export type InvestmentTaxData = {
  investmentId: string;
  investmentName: string;
  lots: LotInput[];
};

let lotIdCounter = 0;

const getNextLotId = (): string => `lot-${Date.now()}-${lotIdCounter++}`;

export const getPricePerShare = (lot: LotInput): number | undefined => {
  if (lot.marketValue <= 0 || lot.quantity <= 0) return undefined;
  return lot.marketValue / lot.quantity;
};

export const createDefaultLot = (): LotInput => ({
  id: getNextLotId(),
  holdingPeriod: { type: 'FromDate', datePurchased: undefined },
  taxStatus: 'TAXABLE',
  marketValue: 0,
  costBasis: 0,
  quantity: 0,
  isNew: true,
});

const HOLDING_PERIOD_TYPE_TO_API: Record<HoldingPeriodInput['type'], string> = {
  FromDate: 'FROM_DATE',
  LongTerm: 'LONG_TERM',
  ShortTerm: 'SHORT_TERM',
};

export const convertLotsToApi = (lots: LotInput[]): Lot[] =>
  lots
    .filter((lot) => lot.marketValue !== 0 || lot.costBasis !== 0 || lot.quantity !== 0)
    .map((lot) => ({
      costBasis: lot.costBasis,
      holdingPeriod:
        lot.holdingPeriod.type === 'FromDate'
          ? { type: HOLDING_PERIOD_TYPE_TO_API.FromDate, datePurchased: lot.holdingPeriod.datePurchased ?? Date.now() }
          : { type: HOLDING_PERIOD_TYPE_TO_API[lot.holdingPeriod.type] },
      quantity: lot.quantity,
      taxStatus: lot.taxStatus,
      marketValue: lot.marketValue,
    }));

export const lotsToInputs = (lots: Lot[]): LotInput[] =>
  lots.map((lot) => ({
    id: getNextLotId(),
    holdingPeriod:
      lot.holdingPeriod?.type === 'FROM_DATE'
        ? { type: 'FromDate' as const, datePurchased: lot.holdingPeriod.datePurchased }
        : lot.holdingPeriod?.type === 'LONG_TERM'
          ? { type: 'LongTerm' as const }
          : { type: 'ShortTerm' as const },
    taxStatus: lot.taxStatus ?? 'TAXABLE',
    marketValue: lot.marketValue ?? 0,
    costBasis: lot.costBasis ?? 0,
    quantity: lot.quantity ?? 0,
    isNew: false,
  }));

export const buildTaxAllocations = (selectedItems: SearchMenuItem[], allLots: LotInput[][]): Map<string, number> => {
  const allocations = new Map<string, number>();
  selectedItems.forEach((item, index) => {
    const lots = allLots[index] ?? [];
    const totalMarketValue = lots.reduce((sum, lot) => sum + lot.marketValue, 0);
    const fundId = item.value?.fund?.id;
    if (fundId) {
      allocations.set(fundId, totalMarketValue);
    }
  });
  return allocations;
};

export const buildTaxLots = (selectedItems: SearchMenuItem[], allLots: LotInput[][]): Map<string, Lot[]> => {
  const lotsMap = new Map<string, Lot[]>();
  selectedItems.forEach((item, index) => {
    const lots = allLots[index] ?? [];
    const fundId = item.value?.fund?.id;
    if (fundId) {
      lotsMap.set(fundId, convertLotsToApi(lots));
    }
  });
  return lotsMap;
};
